import { useMemo } from 'react';

import type { HitlItem, RunEvent, RunStatus, SchemaDriftReport } from '../services/types';

export interface MetricsPanelProps {
  /** WS event buffer for the current run — from ``useRunEvents``. */
  events: RunEvent[];
  /** HITL items on the run — open + resolved + escalated. */
  hits: HitlItem[] | undefined;
  /** Status, anomalies, drift — from the run summary. */
  run: {
    status?: RunStatus;
    progressPct?: number;
    run_result_status?: string;
    anomaly_count?: number;
    schema_drift?: SchemaDriftReport | null;
    /**
     * v0.4 Stage D — cross-field validator output count. ``null`` /
     * ``undefined`` means no rules applied to this run; the
     * "Cross-field flags" stat hides entirely. ``0`` means rules
     * ran but found no violations — rendered as a positive signal
     * (muted tone, "0 flags"). ``> 0`` means violations to review,
     * rendered with a warn tone.
     */
    cross_field_flag_count?: number | null;
  };
}

/**
 * Per-run observability surface.
 *
 * Aggregates signals the reviewer UI already has (the WS event stream,
 * the HITL queue, the run summary) into a single snapshot so the
 * reviewer doesn't have to hop between tabs to understand the run's
 * health.
 *
 * Displayed blocks
 * ----------------
 * 1. Headline: status, executor status, anomaly count.
 * 2. Stage timings: elapsed time per pipeline stage derived from
 *    progress-event timestamps + a sparkline of progress % over time.
 * 3. HITL breakdown: open / resolved / escalated counts, by severity
 *    and by agent. Answers "where did the friction come from?"
 * 4. Schema drift: dormant in v0.1 (reads ``run.schema_drift`` which
 *    is always ``null``); v0.2 populates this once baselines persist.
 *
 * No agent-latency-per-agent panel here — the backend's log events
 * don't carry ``duration_ms``, so we'd have to derive from timestamps
 * between consecutive logs, which gives wall-clock but not CPU time.
 * Plan to add this in v0.2 once the log event shape is extended.
 */
export function MetricsPanel({ events, hits, run }: MetricsPanelProps) {
  // ---------------------------------------------------------------------
  // Derive stage timings from progress events. Each progress event is
  // labelled with the stage's chunk_id (bootstrap / fia / pii_* / ras /
  // moa / complete). Elapsed = timestamp of this event - timestamp of
  // the previous one; the first event is anchored at 0s.
  // ---------------------------------------------------------------------
  const { stageTimings, progressSeries } = useMemo(() => {
    const progress = events.filter((e): e is Extract<RunEvent, { type: 'progress' }> =>
      e.type === 'progress',
    );
    if (progress.length === 0) {
      return { stageTimings: [] as { stage: string; elapsedMs: number }[], progressSeries: [] as number[] };
    }
    const t0 = new Date(progress[0].timestamp).getTime();
    const timings = progress.map((e) => ({
      stage: e.chunk_id,
      elapsedMs: Math.max(0, new Date(e.timestamp).getTime() - t0),
    }));
    const series = progress.map((e) => e.percent);
    return { stageTimings: timings, progressSeries: series };
  }, [events]);

  // ---------------------------------------------------------------------
  // HITL aggregations — answer "how much, how severe, where from."
  // ---------------------------------------------------------------------
  const hitlAgg = useMemo(() => {
    const items = hits ?? [];
    const byStatus = { open: 0, resolved: 0, escalated: 0 };
    const bySeverity: Record<string, number> = { low: 0, medium: 0, high: 0 };
    const byAgent: Record<string, number> = {};
    for (const h of items) {
      if (h.status in byStatus) byStatus[h.status as keyof typeof byStatus] += 1;
      bySeverity[h.severity] = (bySeverity[h.severity] ?? 0) + 1;
      byAgent[h.agent_id] = (byAgent[h.agent_id] ?? 0) + 1;
    }
    return { total: items.length, byStatus, bySeverity, byAgent };
  }, [hits]);

  const driftClass = run.schema_drift?.drift_class ?? 'none';
  const lastStage = stageTimings[stageTimings.length - 1];
  const totalElapsedMs = lastStage?.elapsedMs ?? 0;

  return (
    <section aria-labelledby="metrics-title" className="space-y-6">
      <h2 id="metrics-title" className="text-sm font-semibold">
        Metrics
      </h2>

      {/* Headline */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <Stat
          label="status"
          value={run.status ?? 'queued'}
          tone={toneForStatus(run.status)}
        />
        <Stat
          label="executor"
          value={run.run_result_status ?? '—'}
          tone={run.run_result_status === 'partial' ? 'warn' : 'muted'}
        />
        <Stat
          label="anomalies"
          value={run.anomaly_count ?? 0}
          tone={(run.anomaly_count ?? 0) > 0 ? 'warn' : 'muted'}
        />
        <Stat
          label="elapsed"
          value={totalElapsedMs > 0 ? `${(totalElapsedMs / 1000).toFixed(1)}s` : '—'}
          tone="muted"
        />
      </div>

      {/* v0.4 Stage E.2 — cross-field validator output. Render only when
          a count is present (i.e. confirmed rules applied to this run).
          0 is shown as a positive signal ("rules in force, no
          violations"). */}
      {typeof run.cross_field_flag_count === 'number' && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          <Stat
            label="cross-field flags"
            value={run.cross_field_flag_count}
            tone={run.cross_field_flag_count > 0 ? 'warn' : 'muted'}
          />
        </div>
      )}

      {/* Stage timings + progress sparkline */}
      <div className="rounded-md border border-border p-4 space-y-3">
        <div className="flex items-center justify-between flex-wrap gap-3">
          <h3 className="text-xs uppercase tracking-wide text-muted m-0">
            Pipeline pacing
          </h3>
          <Sparkline
            values={progressSeries}
            ariaLabel={`Progress: ${progressSeries.join(', ')} percent`}
          />
        </div>
        {stageTimings.length > 0 ? (
          <ul className="text-xs font-mono space-y-0.5">
            {stageTimings.map((t, i) => (
              <li
                key={`${t.stage}-${i}`}
                className="flex justify-between gap-4"
              >
                <span>{t.stage}</span>
                <span className="text-muted">
                  {(t.elapsedMs / 1000).toFixed(2)}s
                </span>
              </li>
            ))}
          </ul>
        ) : (
          <p className="text-xs text-muted">
            No progress events yet — timings appear as the pipeline runs.
          </p>
        )}
      </div>

      {/* HITL breakdown */}
      <div className="rounded-md border border-border p-4 space-y-3">
        <h3 className="text-xs uppercase tracking-wide text-muted m-0">
          HITL ({hitlAgg.total})
        </h3>
        <div className="grid grid-cols-2 md:grid-cols-3 gap-3 text-sm">
          <KeyedList
            label="by status"
            entries={[
              ['open', hitlAgg.byStatus.open],
              ['resolved', hitlAgg.byStatus.resolved],
              ['escalated', hitlAgg.byStatus.escalated],
            ]}
          />
          <KeyedList
            label="by severity"
            entries={[
              ['high', hitlAgg.bySeverity.high ?? 0],
              ['medium', hitlAgg.bySeverity.medium ?? 0],
              ['low', hitlAgg.bySeverity.low ?? 0],
            ]}
          />
          <KeyedList
            label="by agent"
            entries={
              Object.entries(hitlAgg.byAgent).sort(
                (a, b) => b[1] - a[1],
              ) as [string, number][]
            }
            emptyCopy="(no HITL items)"
          />
        </div>
      </div>

      {/* Schema drift — dormant in v0.1 but surfaced so reviewers know
          we're watching. The SchemaDriftBanner on the Schema tab
          handles the visible warning when drift fires. */}
      <div className="rounded-md border border-border p-4 space-y-2">
        <h3 className="text-xs uppercase tracking-wide text-muted m-0">
          Schema drift
        </h3>
        <p className="text-sm">
          <span className="font-mono">{driftClass}</span>
          {driftClass === 'none' && (
            <span className="text-muted text-xs ml-2">
              (no drift detected; v0.1 has no cross-run baseline — see DECISIONS.md)
            </span>
          )}
        </p>
        {run.schema_drift?.entries && run.schema_drift.entries.length > 0 && (
          <ul className="text-xs font-mono space-y-0.5">
            {run.schema_drift.entries.map((e, i) => (
              <li key={`${e.column}-${e.kind}-${i}`}>
                {e.column} · {e.kind} · {e.severity}
              </li>
            ))}
          </ul>
        )}
      </div>
    </section>
  );
}

// ---------------------------------------------------------------------------
// Presentational helpers
// ---------------------------------------------------------------------------

function Stat({
  label,
  value,
  tone,
}: {
  label: string;
  value: string | number;
  tone: 'muted' | 'warn' | 'danger' | 'success';
}) {
  const toneCls = {
    muted: 'text-muted',
    warn: 'text-warn',
    danger: 'text-danger',
    success: 'text-brand-dark',
  }[tone];
  return (
    <div className="rounded-md border border-border p-3">
      <div className="text-xs uppercase tracking-wide text-muted">{label}</div>
      <div className={`font-mono text-sm ${toneCls}`}>{value}</div>
    </div>
  );
}

function KeyedList({
  label,
  entries,
  emptyCopy = '—',
}: {
  label: string;
  entries: [string, number][];
  emptyCopy?: string;
}) {
  return (
    <div>
      <div className="text-xs uppercase tracking-wide text-muted mb-1">
        {label}
      </div>
      {entries.length === 0 ? (
        <p className="text-xs text-muted">{emptyCopy}</p>
      ) : (
        <ul className="text-xs font-mono space-y-0.5">
          {entries.map(([k, v]) => (
            <li key={k} className="flex justify-between gap-4">
              <span>{k}</span>
              <span className="text-muted">{v}</span>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

/**
 * Tiny inline SVG sparkline — one <polyline> over the data range.
 *
 * Fixed 96×24 viewBox keeps it lightweight in the table-cell / badge
 * contexts we use it in. Empty / single-point series render an empty
 * box to preserve layout.
 */
function Sparkline({
  values,
  ariaLabel,
  width = 96,
  height = 24,
}: {
  values: number[];
  ariaLabel: string;
  width?: number;
  height?: number;
}) {
  if (values.length < 2) {
    return (
      <svg
        width={width}
        height={height}
        aria-label={ariaLabel}
        role="img"
        className="text-muted"
      >
        <line
          x1={0}
          y1={height - 1}
          x2={width}
          y2={height - 1}
          stroke="currentColor"
          strokeWidth={1}
          opacity={0.3}
        />
      </svg>
    );
  }
  const min = Math.min(...values);
  const max = Math.max(...values);
  const span = max - min || 1;
  const pad = 2;
  const stepX = (width - 2 * pad) / (values.length - 1);
  const points = values
    .map((v, i) => {
      const x = pad + i * stepX;
      const y = pad + (height - 2 * pad) * (1 - (v - min) / span);
      return `${x.toFixed(1)},${y.toFixed(1)}`;
    })
    .join(' ');
  return (
    <svg
      width={width}
      height={height}
      aria-label={ariaLabel}
      role="img"
      className="text-brand-dark"
    >
      <polyline
        points={points}
        fill="none"
        stroke="currentColor"
        strokeWidth={1.25}
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}

function toneForStatus(s: RunStatus | undefined): 'muted' | 'warn' | 'danger' | 'success' {
  switch (s) {
    case 'complete':
      return 'success';
    case 'error':
      return 'danger';
    case 'awaiting_review':
      return 'warn';
    default:
      return 'muted';
  }
}
